import { OVERVIEW_SCREENSHOT_FIELDS, OVERVIEW_SCREENSHOTS_QUERY_LIMIT } from "../../../../models/looker/modules/overview";
export const FILTER_OPTIONS_ADVERTISER_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    // product_group is fetched so advertisers can be greyed by media type; the
    // query itself is intentionally NOT filtered by media type so the full
    // advertiser list is always returned.
    fields: [
        "meta_inst.advertiser_id",
        "meta_inst.advertiser_name",
        "explore_overview.product_group"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    sorts: ["meta_inst.advertiser_name asc"],
    limit: "-1"
};
// The template deliberately has no flt_date_range: the handler must set it
// from the selected dates (the query type requires it). An unset range makes
// explore_overview silently fall back to the last 30 days, which hid products
// from older ranges. The caller shows a fixed list of every product group and
// greys those absent from this result.
export const FILTER_OPTIONS_MEDIA_TYPE_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["explore_overview.product_group"],
    filters: {
        "explore_overview.product_group": "-NULL,-EMPTY"
    },
    sorts: ["explore_overview.product_group asc"],
    limit: "500"
};
export const FILTER_OPTIONS_CAMPAIGN_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["meta_inst.campaign_id", "meta_inst.campaign_name"],
    filters: {
        "explore_overview.flt_date_range": "",
        "meta_inst.advertiser_id": ""
    },
    sorts: ["meta_inst.campaign_name asc"],
    limit: "-1"
};
export const PARTNER_LOGO_QUERY = {
    model: "omnichannel_analytics",
    // Its own explore, not explore_overview: the logo is partner-scoped rather
    // than delivery-scoped, so it is not reachable from the delivery explore.
    view: "viewer_partner_logo",
    fields: ["viewer_partner_logo.logo_url"],
    // No date filter: the partner's logo isn't date-scoped. No sort: the logo
    // URL is expected to be identical across all of a partner's rows, so
    // there's nothing to order by — ordering would force an expensive sort
    // before LIMIT 1 on an unindexed field for no benefit.
    filters: {
        "viewer_partner_logo.logo_url": "-NULL,-EMPTY"
    },
    limit: "1"
};
export const TOP_LINE_METRICS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.total_impressions",
        "explore_overview.total_video_completes",
        "explore_overview.total_clicks",
        "explore_overview.site_visits",
        "explore_overview.view_through_conversions"
    ],
    filters: {
        "explore_overview.flt_date_range": "",
        "explore_overview.pmt_lookback_window": ""
    },
    limit: "1"
};
// Calls (SEM) is fetched in its own query, deliberately separate from the five
// metrics above. calls.total_calls comes from a joined view; selecting it
// alongside the explore_overview aggregates risks fan-out multiplying them, so
// it is isolated here where it can only affect its own value. Same filters as
// the metrics query so both reflect the same selection.
export const TOP_LINE_CALLS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["calls.total_calls"],
    filters: {
        "explore_overview.flt_date_range": "",
        "explore_overview.pmt_lookback_window": ""
    },
    limit: "1"
};
export const PLACEMENTS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.product_group",
        "explore_overview.bundle",
        "explore_overview.total_impressions",
        "explore_overview.retargeting_impressions",
        "explore_overview.total_reach",
        "explore_overview.total_clicks",
        "explore_overview.click_rate"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    // Sort by media-type group first, then impressions desc within each group so
    // the nested bundle (inner-table) rows read high-to-low under their parent.
    sorts: ["explore_overview.product_group", "explore_overview.total_impressions desc"],
    // Unlimited (matches the sibling overview list queries). The widget computes
    // group and grand totals by summing the returned rows, so a fixed cap would
    // silently drop bundle rows and understate those totals.
    limit: "-1"
};
// Reach per media-type group, computed by Looker at the product_group grain
// (see the Placements handler for why reach can't be summed from bundle rows).
export const PLACEMENTS_REACH_BY_GROUP_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["explore_overview.product_group", "explore_overview.total_reach"],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    sorts: ["explore_overview.product_group"],
    limit: "500"
};
// Grand-total reach across all placements, at its own Looker grain.
export const PLACEMENTS_TOTAL_REACH_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["explore_overview.total_reach"],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    limit: "1"
};
export const PERFORMANCE_BY_PRODUCT_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.product_group",
        "explore_overview.total_impressions",
        "explore_overview.total_video_completes",
        "explore_overview.total_reach",
        "explore_overview.avg_frequency",
        "explore_overview.total_cost",
        "explore_overview.cpa",
        "explore_overview.total_conversions",
        "explore_overview.roas"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    // One row per product group, biggest first so the data window keeps the most
    // significant products.
    sorts: ["explore_overview.total_impressions desc"],
    limit: "500"
};
export const QUARTILES_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    // Both media funnels in one query; the widget's tab decides which to show.
    fields: [
        "explore_overview.video_only_impressions",
        "explore_overview.video_only_starts",
        "explore_overview.video_only_q1",
        "explore_overview.video_only_q2",
        "explore_overview.video_only_q3",
        "explore_overview.video_only_completes",
        "explore_overview.audio_only_impressions",
        "explore_overview.audio_only_starts",
        "explore_overview.audio_only_q1",
        "explore_overview.audio_only_q2",
        "explore_overview.audio_only_q3",
        "explore_overview.audio_only_completes"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    limit: "1"
};
export const GEO_MAP_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.location",
        "explore_overview.city",
        // ZIP/ZCTA for the ZIP-level choropleth. Adding it groups rows by
        // (location, postal); impressions/clicks still sum correctly by ZIP.
        "explore_overview.postal",
        "explore_overview.selected_metric",
        "explore_overview.total_impressions",
        "explore_overview.total_clicks",
        "explore_overview.vcr"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    // Highest-impression points first so the row cap keeps the most significant
    // locations if a market returns more points than the limit.
    sorts: ["explore_overview.total_impressions desc"],
    // Location granularity can be fine; cap generously. Anything beyond this is
    // the long tail of low-impression points.
    limit: "5000"
};
/**
 * ZIP (ZCTA) choropleth totals, grouped by `postal` so each row is one ZIP with
 * its true impression/click sums. This drives the fill shading and ZIP popup,
 * independent of the capped point-level query (which can drop locations within a
 * ZIP). Per-ZIP rows are far fewer than per-location, so the limit comfortably
 * covers an advertiser/campaign's ZIP footprint.
 */
export const GEO_MAP_ZCTA_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.postal",
        "explore_overview.total_impressions",
        "explore_overview.total_clicks"
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    sorts: ["explore_overview.total_impressions desc"],
    limit: "5000"
};
export const GEO_MAP_GEOFENCE_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: ["explore_overview.geofence_multipoints"],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    // One aggregated FeatureCollection row for the whole advertiser+campaign
    // selection; never fans out, so a single row is expected.
    limit: "1"
};
/** Looker field names for the Campaign Delivery Status query. */
export const CAMPAIGN_STATUS_FIELD = "meta_inst.campaign_status";
export const CAMPAIGN_COUNT_FIELD = "meta_inst.campaign_count";
/**
 * Base for every "breakdown" (metric-by-dimension) query. The Breakdown handler
 * appends the grouping `fields` (the dimension plus all metric measures) and the
 * `sorts` per dimension, then selects the requested metric and computes shares
 * client-side, so one shape serves Device Distribution, Line Item, Daypart, and
 * future dimensions.
 */
export const OVERVIEW_BREAKDOWN_QUERY_BASE = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    // Finite limit: dev Looker refuses unlimited ("-1") non-streamable queries.
    // 500 comfortably covers every breakdown dimension's bucket count.
    limit: "500"
};
export const CAMPAIGN_DELIVERY_STATUS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [CAMPAIGN_STATUS_FIELD, CAMPAIGN_COUNT_FIELD],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    sorts: [`${CAMPAIGN_COUNT_FIELD} desc`],
    limit: "500"
};
/**
 * Impressions per day split by product group. product_group is requested as a
 * plain field rather than a Looker pivot: a pivoted response keys its columns
 * by "<pivot value> - <measure>", which is fiddly to parse, while long-format
 * rows pivot cleanly in the handler.
 */
export const OVERVIEW_DAILY_IMPRESSIONS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.date_date",
        "explore_overview.product_group",
        "explore_overview.total_impressions"
    ],
    // Descending, then re-sorted ascending in the handler. The row cap below is
    // days x products, and the date picker allows up to two years, so a wide
    // all-product range can exceed it. Truncation then drops the tail of the
    // sort — which must be the oldest dates, not the most recent ones, since a
    // trend chart missing its latest days reads as delivery having stopped.
    sorts: ["explore_overview.date_date desc"],
    limit: "5000",
    filters: {
        "explore_overview.flt_date_range": ""
    }
};
export const OVERVIEW_SPEND_ANALYSIS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        "explore_overview.date_date",
        "explore_overview.product_group",
        "explore_overview.total_cost"
    ],
    // Descending so a range exceeding the row cap drops the oldest dates, not the
    // most recent; the handler re-sorts ascending for the trend line.
    sorts: ["explore_overview.date_date desc"],
    limit: "5000",
    filters: {
        "explore_overview.flt_date_range": ""
    }
};
/**
 * Finite limit, unlike the sibling `-1` queries: this returns a row per capture
 * rather than a bounded list, and dev Looker refuses unlimited non-streamable
 * queries. The field prefix differs from the filters' because the screenshot
 * dimensions live on a joined view, not on explore_overview.
 */
export const SCREENSHOTS_QUERY = {
    model: "omnichannel_analytics",
    view: "explore_overview",
    fields: [
        OVERVIEW_SCREENSHOT_FIELDS.publisher,
        OVERVIEW_SCREENSHOT_FIELDS.imageUrl,
        OVERVIEW_SCREENSHOT_FIELDS.capturedAt
    ],
    filters: {
        "explore_overview.flt_date_range": ""
    },
    sorts: [`${OVERVIEW_SCREENSHOT_FIELDS.capturedAt} desc`],
    limit: String(OVERVIEW_SCREENSHOTS_QUERY_LIMIT)
};
//# sourceMappingURL=query.js.map